import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { HelmCompass } from "@/components/helm-logo";
import { Button } from "@/components/ui/button";
import { apiService } from "@/lib/api-service";
import {
	trackOnboardingGetStartedCompleted,
	trackOnboardingGetStartedViewed,
	trackPlatformIntroCompleted,
} from "@/lib/analytics-events";

export const Route = createFileRoute("/_app/_/onboarding/get-started")({
	component: GetStartedPage,
});

function GetStartedPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useEffect(() => {
		trackOnboardingGetStartedViewed();
	}, []);

	const completeMutation = useMutation({
		mutationFn: () => apiService.completePlatformIntro(),
		onSuccess: () => {
			trackPlatformIntroCompleted(false);
			trackOnboardingGetStartedCompleted();
			queryClient.invalidateQueries({ queryKey: ["session"] });
			navigate({ to: "/onboarding/interview-intro" });
		},
	});

	return (
		<div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-[var(--strategy-surface)]">
			<div className="max-w-2xl w-full text-center space-y-12 transition-all duration-700 opacity-100 translate-y-0">
				{/* Logo */}
				<div className="flex flex-col items-center">
					<div className="flex items-center gap-6">
						<HelmCompass size={72} strokeWidth={1} />
						<span
							className="font-display text-6xl"
							style={{ letterSpacing: "0.25em" }}
						>
							H E L M
						</span>
					</div>
				</div>

				{/* Divider */}
				<div className="border-t border-border mx-auto w-48" />

				{/* Copy */}
				<div className="space-y-4">
					<p className="text-xl md:text-2xl text-muted-foreground">
						Let's show you around your campaign headquarters.
					</p>
					<p className="text-lg text-muted-foreground/80 leading-relaxed">
						Tools that turn strategy into action—
						<br />
						<span className="font-display-italic text-foreground">
							personalized for your race.
						</span>
					</p>
				</div>

				{/* CTA */}
				<Button
					size="lg"
					className="h-14 px-10 text-base"
					onClick={() => completeMutation.mutate()}
					disabled={completeMutation.isPending}
				>
					Start Onboarding
					<ArrowRight className="ml-2 h-5 w-5" />
				</Button>
			</div>
		</div>
	);
}
